import { useCallback, useState, useEffect, useMemo } from 'react';
import { Promise } from 'bluebird';
import { createColorBackground, fetchImgPromise, parseMaskResultPromise } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { useMaskedImage } from './use-masked-image';

export const useBackgroundChange = ({
  dataService,
  perfMarker,
  logger,
}) => {
  const [fetchSegmentationError, setFetchSegmentationError] = useState('');
  const [isFetchingSegmentations, setIsFetchingSegmentations] = useState(false);

  const {
    getImageSegmentations,
    generateImageBackground,
  } = dataService;

  const {
    alphaArray,
    imageUrlWithoutBackground,
    setImageUrlWithoutBackground,
    imageUrlWithGeneratedBackground,
    setImageUrlWithGeneratedBackground,
    imageUrlWithColoredBackground,
    setImageUrlWithColoredBackground,
    segmentationMasks,
    setSegmentationMasks,
    getMaskValueAtPoint,
    selectedMasks,
    setSelectedMasks,
    selectedImageSrc,
    setSelectedImageSrc,
    maskSetsBase64,
    setMaskSetsBase64,
    resetStates,
    maskValues,
  } = useMaskedImage();

  const drawColorBackground = useCallback((color, imageWidth, imageHeight) => {
    if ((selectedImageSrc || imageUrlWithoutBackground) && segmentationMasks) {
      return fetchImgPromise(selectedImageSrc || imageUrlWithoutBackground).then((image) => {
        const coloredBackground = createColorBackground(image, imageWidth, imageHeight, color, segmentationMasks, selectedMasks, alphaArray);
        setImageUrlWithColoredBackground(coloredBackground);
      });
    }
    return Promise.resolve(null);
  }, [selectedImageSrc, imageUrlWithoutBackground, segmentationMasks, selectedMasks, alphaArray, setImageUrlWithColoredBackground]);

  const getSegmentations = useCallback(({
    imageUrl, imageWidth, imageHeight,
  }) => {
    const imageSegmentationPerfMarker = perfMarker.createChild('ImageSegmentations');
    imageSegmentationPerfMarker.willInit();
    setIsFetchingSegmentations(true);
    resetStates();
    const ret = getImageSegmentations(imageUrl).then(result => (
      parseMaskResultPromise({
        result,
        imageUrl,
        imageWidth,
        imageHeight,
      })
    )).then((parsedResult) => {
      const {
        rawMaskSetsBase64String: rawMaskSetsBase64,
        maskSetsArray,
        selectedMasksArray,
        alphaArray: newAlphaArray,
        imageUrlWithoutBackground: selfDrawnImageUrl,
      } = parsedResult || {};
      setImageUrlWithoutBackground(selfDrawnImageUrl);
      setSegmentationMasks({ maskSetsArray, selectedMasksArray, newAlphaArray });
      setMaskSetsBase64(rawMaskSetsBase64);
      logger.logTrace('Copilot change image background - get image segmentations successfully', { originalUrl: imageUrl });
    }).catch((err) => {
      setFetchSegmentationError(err.uxMessage);
    })
      .finally(() => {
        setIsFetchingSegmentations(false);
        imageSegmentationPerfMarker.done();
      });
    return ret;
  }, [resetStates, perfMarker,
    getImageSegmentations, setImageUrlWithoutBackground, setSegmentationMasks, setMaskSetsBase64, logger]);

  const generateBackground = useCallback(({
    prompt, imageUrl, imageWidth, imageHeight,
  }) => {
    const backgroundGenerationPerfMarker = perfMarker.createChild('ImageBackgroundGeneration');
    backgroundGenerationPerfMarker.willInit();
    const ret = generateImageBackground({
      prompt,
      imageUrl,
      selectedMasks,
      maskSetsBase64,
      outputImageWidth: imageWidth,
      outputImageHeight: imageHeight,
    }).then((res) => {
      const { imageUrl: newImageUrl } = res || {};
      logger.logTrace('Copilot change image background - generate image background successfully', { generatedUrl: newImageUrl, prompt });
      setImageUrlWithGeneratedBackground(newImageUrl);
      return newImageUrl;
    }).finally(() => {
      backgroundGenerationPerfMarker.done();
    });
    return ret;
  }, [selectedMasks, perfMarker,
    generateImageBackground, maskSetsBase64, logger, setImageUrlWithGeneratedBackground]);

  useEffect(() => {
    logger.logTrace('Copilot change image background - user entered the page');
  }, [logger]);

  const resultImage = useMemo(() => (imageUrlWithGeneratedBackground && {
    url: imageUrlWithGeneratedBackground,
  }), [imageUrlWithGeneratedBackground]);

  return {
    // APIs
    getSegmentations,
    isFetchingSegmentations,
    generateBackground,
    drawColorBackground,

    // image states
    imageUrlWithoutBackground, // the image url without background
    imageUrlWithGeneratedBackground,
    setImageUrlWithGeneratedBackground,
    imageUrlWithColoredBackground,
    resultImage,
    getMaskValueAtPoint,
    segmentationMasks,
    setSelectedImageSrc,
    selectedImageSrc, // the image after user selected objects
    selectedMasks,
    setSelectedMasks,
    maskValues,
    fetchSegmentationError,
  };
};
